// Server-safe OKLCH math + brand-ramp generation.
//
// Matches the algorithm in
// `packages/design-system/src/providers/theme-provider/theme-color-helper/generate-ramp.ts`
// so the API, backfill scripts, and the React editor all compute the same
// ramp from a given base hex. The design-system module outputs RGB triplets
// for direct CSS-variable injection; this module outputs hex stops because
// hex is what we store in the database and what the editor's color inputs
// consume natively.

export type Oklch = { l: number; c: number; h: number };
export type Rgb = { r: number; g: number; b: number };
export type ColorRampKind = "primary" | "accent";

export type RampStop = 100 | 150 | 200 | 300 | 400 | 500 | 600 | 700 | 800 | 900;

export type ColorRamp = {
	base: string;
	stops: Record<number, string>;
	overrides: number[];
};

export const PRIMARY_RAMP_KEYS: RampStop[] = [100, 200, 300, 400, 500, 600, 700, 800, 900];
export const ACCENT_RAMP_KEYS: RampStop[] = [100, 150, 200, 300, 400, 500, 600, 700, 800, 900];

// Lightness ladder anchored at the .600 stop (= input brand color).
// Mirrors `L_DELTAS` in generate-ramp.ts.
const L_DELTAS: Record<RampStop, number> = {
	100: 0.47,
	150: 0.42,
	200: 0.38,
	300: 0.28,
	400: 0.2,
	500: 0.11,
	600: 0,
	700: -0.11,
	800: -0.23,
	900: -0.38,
};

// Chroma multipliers — keep full chroma near the anchor, soften toward gray
// at the extremes to avoid neon banding and gamut clipping.
const C_MULTIPLIERS: Record<RampStop, number> = {
	100: 0.15,
	150: 0.25,
	200: 0.4,
	300: 0.65,
	400: 0.85,
	500: 0.95,
	600: 1,
	700: 0.95,
	800: 0.7,
	900: 0.45,
};

const HEX_RE = /^#[0-9a-fA-F]{6}$/;

export const isValidHex = (hex: string): boolean => HEX_RE.test(hex);

const normalizeHex = (hex: string): string => {
	const value = hex.startsWith("#") ? hex.slice(1) : hex;
	const expanded =
		value.length === 3
			? value
					.split("")
					.map((c) => c + c)
					.join("")
			: value;
	return `#${expanded.toUpperCase()}`;
};

const clamp = (n: number, min: number, max: number) => Math.max(min, Math.min(max, n));

const srgbToLinear = (v: number): number => {
	const n = v / 255;
	return n <= 0.04045 ? n / 12.92 : ((n + 0.055) / 1.055) ** 2.4;
};

const linearToSrgb = (v: number): number => {
	const n = v <= 0.0031308 ? 12.92 * v : 1.055 * v ** (1 / 2.4) - 0.055;
	return clamp(Math.round(n * 255), 0, 255);
};

export const rgbToOklch = ({ r, g, b }: Rgb): Oklch => {
	const lr = srgbToLinear(r);
	const lg = srgbToLinear(g);
	const lb = srgbToLinear(b);

	const lp = Math.cbrt(0.4122214708 * lr + 0.5363325363 * lg + 0.0514459929 * lb);
	const mp = Math.cbrt(0.2119034982 * lr + 0.6806995451 * lg + 0.1073969566 * lb);
	const sp = Math.cbrt(0.0883024619 * lr + 0.2817188376 * lg + 0.6299787005 * lb);

	const L = 0.2104542553 * lp + 0.793617785 * mp - 0.0040720468 * sp;
	const a = 1.9779984951 * lp - 2.428592205 * mp + 0.4505937099 * sp;
	const bb = 0.0259040371 * lp + 0.7827717662 * mp - 0.808675766 * sp;

	const C = Math.sqrt(a * a + bb * bb);
	let H = (Math.atan2(bb, a) * 180) / Math.PI;
	if (H < 0) {
		H += 360;
	}
	return { l: L, c: C, h: H };
};

export const oklchToRgb = ({ l, c, h }: Oklch): Rgb => {
	const a = c * Math.cos((h * Math.PI) / 180);
	const b = c * Math.sin((h * Math.PI) / 180);

	const lp = (l + 0.3963377774 * a + 0.2158037573 * b) ** 3;
	const mp = (l - 0.1055613458 * a - 0.0638541728 * b) ** 3;
	const sp = (l - 0.0894841775 * a - 1.291485548 * b) ** 3;

	const lr = 4.0767416621 * lp - 3.3077115913 * mp + 0.2309699292 * sp;
	const lg = -1.2684380046 * lp + 2.6097574011 * mp - 0.3413193965 * sp;
	const lb = -0.0041960863 * lp - 0.7034186147 * mp + 1.707614701 * sp;

	return {
		r: linearToSrgb(lr),
		g: linearToSrgb(lg),
		b: linearToSrgb(lb),
	};
};

const hex2 = (n: number) => n.toString(16).padStart(2, "0");

export const rgbToHex = ({ r, g, b }: Rgb): string => `#${hex2(r)}${hex2(g)}${hex2(b)}`.toUpperCase();

export const hexToRgb = (hex: string): Rgb => {
	const value = normalizeHex(hex).slice(1);
	return {
		r: Number.parseInt(value.slice(0, 2), 16),
		g: Number.parseInt(value.slice(2, 4), 16),
		b: Number.parseInt(value.slice(4, 6), 16),
	};
};

export const hexToOklch = (hex: string): Oklch => rgbToOklch(hexToRgb(hex));
export const oklchToHex = (oklch: Oklch): string => rgbToHex(oklchToRgb(oklch));

export const getRampKeys = (kind: ColorRampKind): RampStop[] =>
	kind === "accent" ? ACCENT_RAMP_KEYS : PRIMARY_RAMP_KEYS;

/**
 * Generate a hex-stop ramp from a brand hex. The input is the .600 anchor;
 * other stops keep H, scale C by `C_MULTIPLIERS`, and adjust L by `L_DELTAS`.
 *
 * Matches the math in design-system's `generateRamp(hex, role)` so the
 * server-side computation (API + backfill script) lines up with what the
 * React renderer would produce.
 */
export const computeRampHex = (hex: string, kind: ColorRampKind): Record<RampStop, string> => {
	const anchor = hexToOklch(hex);
	const stops = getRampKeys(kind);
	const ramp = {} as Record<RampStop, string>;
	for (const stop of stops) {
		const target: Oklch = {
			l: clamp(anchor.l + L_DELTAS[stop], 0, 1),
			c: anchor.c * C_MULTIPLIERS[stop],
			h: anchor.h,
		};
		ramp[stop] = oklchToHex(target);
	}
	return ramp;
};

export const buildRampFromHex = (hex: string, kind: ColorRampKind): ColorRamp => ({
	base: normalizeHex(hex),
	stops: { ...computeRampHex(hex, kind) } as Record<number, string>,
	overrides: [],
});

/**
 * Regenerate a ramp from a (possibly new) base while preserving any stop the
 * user previously customized.
 */
export const regenerateRamp = (
	base: string,
	kind: ColorRampKind,
	previousOverrides: Record<number, string>,
): ColorRamp => {
	const stops = { ...computeRampHex(base, kind) } as Record<number, string>;
	for (const key of Object.keys(previousOverrides)) {
		stops[Number(key)] = previousOverrides[Number(key)] as string;
	}
	return {
		base: normalizeHex(base),
		stops,
		overrides: Object.keys(previousOverrides).map(Number),
	};
};

// WCAG relative luminance from a hex color.
export const relativeLuminance = (hex: string): number => {
	const { r, g, b } = hexToRgb(hex);
	const lr = srgbToLinear(r);
	const lg = srgbToLinear(g);
	const lb = srgbToLinear(b);
	return 0.2126 * lr + 0.7152 * lg + 0.0722 * lb;
};

// Pick "#0F1921" or "#FFFFFF" for text drawn on a colored background.
// Threshold ~0.55 so warm yellows fall on black text and navy on white.
export const pickOnColor = (hex: string): string => (relativeLuminance(hex) > 0.55 ? "#0F1921" : "#FFFFFF");
